<template>
  <RfSimpleCard style="min-height: 400px" class="p-10">
    <div>
      <div class="rf-settings-section-title">
        <h4>Custom Devices Manager</h4>
        <p class="title-blurb">
          Manage custom devices. Only devices with no assigned prompts may be deleted.
        </p>
      </div>
    </div>
    <v-table data-cy="custom-devices--table" class="rf-settings-table-data mt-10">
      <thead>
        <th class="device-table-col">Name</th>
        <th class="device-table-col">Label</th>
        <th class="device-table-col">Description</th>
        <th class="device-table-col"></th>
      </thead>
      <tbody v-if="customDevicesStore.customDevices.length > 0">
        <tr v-for="device in customDevicesStore.customDevices" :key="device.id">
          <td align="center">{{ device.name }}</td>
          <td align="center">{{ device.label }}</td>
          <td>{{ device.description }}</td>
          <td>
            <div class="settings-button-wrapper">
              <RfButton
                :icon="
                  !!userStore.isDisabledWithMessage ? 'mdi:mdi-information-variant' : 'md:edit'
                "
                size="small"
                variant="plain"
                density="compact"
                data-cy="custom-devices--edit"
                color="accent"
                @click="openEditCustomDevice(device)"
              />
              <RfButton
                icon="md:clear"
                size="small"
                variant="plain"
                density="compact"
                data-cy="custom-devices--delete"
                color="error"
                :disabled="userStore.isDisabledWithMessage"
                @click="openDeleteCustomDevice(device)"
              />
            </div>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr>
          <td colspan="4">
            <div class="settings-no-data-message">Nothing to show</div>
          </td>
        </tr>
      </tbody>
    </v-table>
    <!-- modals -->
    <v-dialog
      v-model="createEditModal"
      persistent
      width="640"
      contentClass="create-device-dialog"
      overlayOpacity="0.85"
    >
      <v-card flat>
        <v-card-title class="headline">
          {{ device.id ? "Edit custom device" : "Create new custom device" }}
        </v-card-title>
        <v-card-text>
          <RfTextInput
            v-model.trim="device.name"
            density="compact"
            variant="outlined"
            persistentHint
            autofocus
            data-cy="custom-device-modal--input-name"
            placeholder="Name"
            :disabled="userStore.isReadOnly"
            title="Name"
            hint="e.g., Android (All)"
          />
          <RfTextInput
            v-model.trim="device.label"
            density="compact"
            variant="outlined"
            persistentHint
            data-cy="custom-device-modal--input-label"
            placeholder="Label"
            :disabled="userStore.isReadOnly"
            title="Label"
            hint="e.g., android_all"
          />
          <RfTextInput
            v-model.trim="device.description"
            density="compact"
            variant="outlined"
            persistentHint
            data-cy="custom-device-modal--input-description"
            placeholder="Description"
            :disabled="userStore.isReadOnly"
            title="Description"
            hint="e.g., Used for all android-based devices"
          />
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn
            size="large"
            class="cancel-btn"
            variant="outlined"
            width="100px"
            @click="closeModals"
          >
            {{ !userStore.isDisabledWithMessage ? "Cancel" : "Close" }}
          </v-btn>
          <RfButton
            icon="md:save"
            :buttonText="device.id ? 'Update Custom Device' : 'Create new custom device'"
            color="success"
            data-cy="custom-device-modal--submit"
            size="large"
            :loading="loading"
            width="260px"
            :disabled="userStore.isDisabledWithMessage || loading"
            @click="submitCustomDevice"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteModal" persistent width="500">
      <v-card>
        <v-card-title class="headline">
          <span>
            Are you sure you want to delete <b>{{ device.name }}</b
            >?
          </span>
        </v-card-title>
        <v-card-text>
          Deleting this custom device removes all information about this custom device. This cannot
          be undone.
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn class="cancel-btn px-7!" variant="outlined" @click="closeModals"> Cancel </v-btn>
          <v-btn
            variant="flat"
            :loading="loading"
            :disabled="loading"
            color="error"
            data-cy="custom-device-delete-modal--confirm"
            class="px-10!"
            @click="submitDeleteCustomDevice"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </RfSimpleCard>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import RfButton from "@/components/buttons/RfButton.vue";
import RfSimpleCard from "@/components/RfSimpleCard.vue";
import RfTextInput from "@/components/legacy/RfInputLegacy.vue";
import { useToastsStore } from "@/pinia/toastsStore";
import { useCustomDevicesStore } from "@/pinia/customDevicesStore";
import { useUserStore } from "@/pinia/userStore";

const route = useRoute();
const toastsStore = useToastsStore();
const customDevicesStore = useCustomDevicesStore();
const userStore = useUserStore();

const createEditModal = ref(false);
const deleteModal = ref(false);
const device = ref({ id: null, name: null, description: null, label: null });
const loading = ref(false);

const resetModal = () => {
  device.value = { id: null, name: null, description: null, label: null };
};

const newCustomDevice = () => {
  createEditModal.value = true;
  resetModal();
};

const openEditCustomDevice = ({ id, name, label, description }) => {
  createEditModal.value = true;
  device.value = { id, name, label, description };
};

const openDeleteCustomDevice = ({ id, name, label, description }) => {
  device.value = { id, name, label, description };
  deleteModal.value = true;
};

const closeModals = () => {
  createEditModal.value = false;
  deleteModal.value = false;
  setTimeout(resetModal, 330);
};

const submitCustomDevice = async () => {
  try {
    loading.value = true;
    const appId = route.params.aid;
    const { id: deviceId, name, description, label } = { ...device.value };
    const modelDevice = { name, description, label };

    if (deviceId) {
      await customDevicesStore.updateCustomDevice({ appId, deviceId, modelDevice });
    } else {
      await customDevicesStore.createCustomDevice({ appId, modelDevice });
    }
    closeModals();
  } catch (e) {
    toastsStore.create({ type: "error", body: e.message });
  } finally {
    loading.value = false;
  }
};

const submitDeleteCustomDevice = async () => {
  try {
    loading.value = true;
    const appId = route.params.aid;
    await customDevicesStore.deleteCustomDevice({ appId, deviceId: device.value.id });
    closeModals();
  } catch (e) {
    toastsStore.create({ type: "error", body: e.message });
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  customDevicesStore.getCustomDevices(route.params.aid);
});

defineExpose({ click: newCustomDevice });
</script>
